import { Component, OnDestroy, OnInit } from '@angular/core';
import { KeycloakProfile } from 'keycloak-js';
import IZikataClientProfile from './models/user-profile-model';
import { AuthService } from './core/service/auth.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '../environments/environment';
import {
  INavbarItem,
  PageStateService,
  TokenExpiryService,
} from '@zikata-frontend/ui';

type ParamFunction<T> = (params: Record<string, string>) => T;

interface RouteInfo {
  path: string;
  title: string | ParamFunction<string>;
  breadcrumb: string[] | ParamFunction<string[]>;
}

@Component({
  selector: 'zikata-frontend-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'clients-portal';
  userProfile: KeycloakProfile | undefined;
  userAccount: IZikataClientProfile | undefined;
  isLoggedIn = false;
  currentRoles: string[] = [];
  officerRoles: Record<string, string> = environment.officerRoles;

  private routerSubscription: Subscription;

  private routeMap: Record<number, RouteInfo> = {
    0: {
      path: '/dashboard/home',
      title: 'Home',
      breadcrumb: ['Dashboard', 'Home'],
    },
    3: {
      path: '/dashboard/applications',
      title: 'My Applications',
      breadcrumb: ['Dashboard', 'Applications'],
    },
    9: {
      path: '/dashboard/alerts',
      title: 'Alerts',
      breadcrumb: ['Dashboard', 'Alerts'],
    },
    4: {
      path: '/dashboard/developer',
      title: 'Developer',
      breadcrumb: ['Dashboard', 'Developer'],
    },
    5: {
      path: '/dashboard/transaction',
      title: 'Transactions',
      breadcrumb: ['Dashboard', 'Transactions'],
    },
    6: {
      path: '/dashboard/users',
      title: 'Display Users',
      breadcrumb: ['Dashboard', 'Users'],
    },
    7: {
      path: '/dashboard/contacts',
      title: 'Contacts',
      breadcrumb: ['Dashboard', 'Contacts'],
    },
    8: {
      path: '/dashboard/campaign',
      title: 'SMS Builder',
      breadcrumb: ['Dashboard', 'Campaign'],
    },
    11: {
      path: '/contact-management/campaigns',
      title: 'Bulk SMS',
      breadcrumb: ['Dashboard', 'SMS', 'Bulk SMS'],
    },
    10: {
      path: '/contact-management/groups',
      title: 'Contact Groups',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups'],
    },
    12: {
      path: '/contact-management/surveys',
      title: 'Surveys',
      breadcrumb: ['Dashboard', 'Surveys'],
    },
    100: {
      path: '/contact-management/groups/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Contact Groups`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Contact',
        'Contact Groups',
        'Updating',
      ],
    },
    103: {
      path: '/contact-management/surveys/form/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Survey`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Survey',
        'Updating',
      ],
    },
    105: {
      path: '/contact-management/surveys/questions/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Survey Question`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Survey',
        'Questions',
        'Updating',
      ],
    },
    106: {
      path: '/contact-management/surveys/outro/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Survey Outro`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Survey',
        'Outro',
      ],
    },
    107: {
      path: '/contact-management/surveys/send/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Publish Survey`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Survey',
        'Send Survey',
        'Updating',
      ],
    },
    108: {
      path: '/contact-management/surveys/responses/:id',
      title: (params: Record<string, string>) => {
        console.log('params::', params);
        return `Survey Responses`;
      },
      breadcrumb: (params: Record<string, string>) => [
        'Dashboard',
        'Survey',
        'Responses',
      ],
    },
    101: {
      path: '/contact-management/campaigns/new',
      title: 'Bulk SMS',
      breadcrumb: ['Dashboard', 'SMS', 'Bulk SMS', 'New'],
    },
    104: {
      path: '/contact-management/surveys/form/new',
      title: 'Survey',
      breadcrumb: ['Dashboard', 'Survey', 'New', 'New'],
    },
    109: {
      path: '/contact-management/campaigns/form/:id',
      title: (params: Record<string, string>) => `Edit Bulk SMS Campaign`,
      breadcrumb: ['Dashboard', 'SMS', 'Bulk SMS', 'Updating'],
    },
    110: {
      path: '/contact-management/groups/new',
      title: 'New Contact Group',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'New'],
    },
    111: {
      path: '/contact-management/groups/:id/edit',
      title: (params: Record<string, string>) => 'Edit Contact Group',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'Edit'],
    },
    112: {
      path: '/contact-management/groups/:id/import',
      title: (params: Record<string, string>) => 'Import Contacts',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'Import'],
    },
    113: {
      path: '/contact-management/groups/:id/contacts/new',
      title: (params: Record<string, string>) => 'New Contact',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'New Contact'],
    },
    114: {
      path: '/contact-management/groups/:id/contacts/:contactId/edit',
      title: (params: Record<string, string>) => 'Edit Contact',
      breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'Edit Contact'],
    },
    115: {
      path: '/dashboard/whatsapp',
      title: 'WhatsApp Messages',
      breadcrumb: ['Dashboard', 'WhatsApp'],
    },
    116: {
      path: '/dashboard/summary',
      title: 'Summary',
      breadcrumb: ['Dashboard', 'Summary'],
    },
  };

  constructor(
    private authService: AuthService,
    private router: Router,
    private pageStateService: PageStateService,
    private tokenExpiryService: TokenExpiryService,
  ) {
    this.routerSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        this.updatePageState(event.urlAfterRedirects);
      });
  }

  private setInitialPageState(): void {
    this.updatePageState(this.router.url);
  }

  async ngOnInit(): Promise<void> {
    this.isLoggedIn = await this.authService.isLoggedIn();
    this.currentRoles = this.authService.userRoles();

    if (this.isLoggedIn) {
      this.tokenExpiryService.startWatching();

      this.authService
        .userProfile()
        .then((value) => {
          if (value) {
            this.userProfile = value;
            this.authService.getUserAccount().subscribe((value1) => {
              this.userAccount = value1;
              if (!this.userAccount?.phoneVerified) {
                console.log('Account not verified');
                this.router.navigate(['/auth/activate-account']);
              }
            });
          }
        })
        .catch((reason) => {
          console.error(reason);
        });

      this.setInitialPageState();
      this.updatePageState(this.router.url);
    }
  }

  /**
   * Logout the current user.
   * Stops the token expiry watcher, then delegates to AuthService which
   * clears the Keycloak session. Falls back to a hard redirect if the
   * service call throws.
   */
  logout(): void {
    try {
      this.tokenExpiryService.stopWatching();
      this.authService.logout();
    } catch (error) {
      console.error('[AppComponent] Logout failed:', error);
      window.location.href = window.location.origin + '/login';
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.tokenExpiryService.stopWatching();
  }

  get roles() {
    this.officerRoles = environment.officerRoles;
    return this.currentRoles
      .map((role) => this.officerRoles[role])
      .filter((role) => role !== undefined);
  }

  private updatePageState(path: string): void {
    const urlSegments = path.split('/').filter((segment) => segment);
    let matchedRoute: RouteInfo | undefined;
    const params: Record<string, string> = {};

    for (const route of Object.values(this.routeMap)) {
      const routeSegments = route.path.split('/').filter((segment) => segment);
      if (this.matchRoute(urlSegments, routeSegments, params)) {
        matchedRoute = route;
        break;
      }
    }

    if (matchedRoute) {
      const title =
        typeof matchedRoute.title === 'function'
          ? (matchedRoute.title as ParamFunction<string>)(params)
          : matchedRoute.title;
      const breadcrumb =
        typeof matchedRoute.breadcrumb === 'function'
          ? (matchedRoute.breadcrumb as ParamFunction<string[]>)(params)
          : matchedRoute.breadcrumb;

      this.pageStateService.setPageState({ title, breadcrumb });
    } else {
      this.pageStateService.setPageState({
        title: 'Dashboard',
        breadcrumb: ['Dashboard'],
      });
    }
  }

  private matchRoute(
    urlSegments: string[],
    routeSegments: string[],
    params: Record<string, string>
  ): boolean {
    if (urlSegments.length !== routeSegments.length) return false;

    for (let i = 0; i < routeSegments.length; i++) {
      if (routeSegments[i].startsWith(':')) {
        params[routeSegments[i].slice(1)] = urlSegments[i];
      } else if (routeSegments[i] !== urlSegments[i]) {
        return false;
      }
    }
    return true;
  }
}